import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Stack,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useCategorias } from '../hooks/useCategorias';
import { CategoriaTree } from '../components/CategoriaTree';
import { CategoriaForm } from '../components/CategoriaForm';
import { SubcategoriaForm } from '../components/SubcategoriaForm';
import { CategoriaSearch } from '../components/CategoriaSearch';
import { CreateCategoriaInput, Categoria, Subcategoria, CategoriaWithSubcategorias } from '../types/categoria.types';

type FormMode = 'categoria-create' | 'categoria-edit' | 'subcategoria-create' | 'subcategoria-edit' | null;

interface FormState {
  mode: FormMode;
  categoria?: CategoriaWithSubcategorias | null;
  subcategoria?: Subcategoria | null;
  categoriaIdForSubcategoria?: number;
}

export function CategoriasPage() {
  const {
    categorias,
    filteredCategorias,
    loading,
    error,
    searchQuery,
    fetchTree,
    performSearch,
    createCategoria,
    updateCategoria,
    deleteCategoria,
    createSubcategoria,
    updateSubcategoria,
    deleteSubcategoria,
  } = useCategorias();

  const [formState, setFormState] = useState<FormState>({ mode: null });
  const [deleteConfirm, setDeleteConfirm] = useState<{
    type: 'categoria' | 'subcategoria';
    id: number;
    parentId?: number;
    name: string;
  } | null>(null);

  useEffect(() => {
    fetchTree();
  }, []);

  const displayCategorias = searchQuery && filteredCategorias.length > 0 ? filteredCategorias : categorias;

  // Form handlers
  const handleOpenCategoriaForm = (categoria?: CategoriaWithSubcategorias) => {
    if (categoria) {
      setFormState({
        mode: 'categoria-edit',
        categoria,
      });
    } else {
      setFormState({
        mode: 'categoria-create',
        categoria: null,
      });
    }
  };

  const handleOpenSubcategoriaForm = (
    categoriaId: number,
    subcategoria?: Subcategoria
  ) => {
    if (subcategoria) {
      setFormState({
        mode: 'subcategoria-edit',
        subcategoria,
        categoriaIdForSubcategoria: categoriaId,
      });
    } else {
      setFormState({
        mode: 'subcategoria-create',
        subcategoria: null,
        categoriaIdForSubcategoria: categoriaId,
      });
    }
  };

  const handleCloseForm = () => {
    setFormState({ mode: null });
  };

  const handleSaveCategoria = async (data: CreateCategoriaInput & { activo?: boolean }) => {
    try {
      if (formState.mode === 'categoria-create') {
        await createCategoria(data);
      } else if (formState.mode === 'categoria-edit' && formState.categoria) {
        await updateCategoria(formState.categoria.id, data);
      }
      handleCloseForm();
      await fetchTree();
    } catch (err) {
      // Error is shown in form component
    }
  };

  const handleSaveSubcategoria = async (data: any) => {
    try {
      if (!formState.categoriaIdForSubcategoria) return;

      if (formState.mode === 'subcategoria-create') {
        await createSubcategoria(formState.categoriaIdForSubcategoria, data);
      } else if (formState.mode === 'subcategoria-edit' && formState.subcategoria) {
        await updateSubcategoria(
          formState.categoriaIdForSubcategoria,
          formState.subcategoria.id,
          data
        );
      }
      handleCloseForm();
      await fetchTree();
    } catch (err) {
      // Error is shown in form component
    }
  };

  // Delete handlers
  const handleDeleteClick = (
    type: 'categoria' | 'subcategoria',
    id: number,
    name: string,
    parentId?: number
  ) => {
    setDeleteConfirm({
      type,
      id,
      parentId,
      name,
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirm) return;

    try {
      if (deleteConfirm.type === 'categoria') {
        await deleteCategoria(deleteConfirm.id);
      } else {
        await deleteSubcategoria(deleteConfirm.parentId!, deleteConfirm.id);
      }
      setDeleteConfirm(null);
      await fetchTree();
    } catch (err: any) {
      // Check if it's a conflict error (has active products)
      if (err.status === 409 || err.code === 'CONFLICT') {
        setDeleteConfirm(null);
        // Show error in a way that's visible to user
        // This could be passed to a snackbar or the tree component
        alert(
          `No se puede eliminar: ${deleteConfirm.name}\n\nRazón: ${err.message || 'Tiene productos activos asociados'}`
        );
      }
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 4 }}
      >
        <Box>
          <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
            Categorías y Subcategorías
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Administra la jerarquía de productos
          </Typography>
        </Box>
        <Stack direction="row" gap={1}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => fetchTree()}
            disabled={loading}
          >
            Actualizar
          </Button>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={() => handleOpenCategoriaForm()}
          >
            Nueva Categoría
          </Button>
        </Stack>
      </Stack>

      {/* Search */}
      <Box sx={{ mb: 3 }}>
        <CategoriaSearch
          value={searchQuery}
          onChange={(query) => {
            if (query === '') {
              fetchTree();
            }
          }}
          onSearch={performSearch}
          loading={loading}
          placeholder="Buscar por nombre..."
        />
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tree View */}
      <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 1 }}>
        <CategoriaTree
          categorias={displayCategorias}
          loading={loading}
          onEditCategoria={(categoria) => {
            handleOpenCategoriaForm(categoria);
          }}
          onDeleteCategoria={(id) => {
            const categoria = categorias.find((c) => c.id === id);
            if (categoria) {
              handleDeleteClick('categoria', id, categoria.nombre);
            }
          }}
          onAddSubcategoria={(categoriaId) => {
            handleOpenSubcategoriaForm(categoriaId);
          }}
          onEditSubcategoria={(categoriaId, subId) => {
            const categoria = categorias.find((c) => c.id === categoriaId);
            const subcategoria = categoria?.subcategorias?.find(
              (s) => s.id === subId
            );
            if (subcategoria) {
              handleOpenSubcategoriaForm(categoriaId, subcategoria);
            }
          }}
          onDeleteSubcategoria={(categoriaId, subId) => {
            const categoria = categorias.find((c) => c.id === categoriaId);
            const subcategoria = categoria?.subcategorias?.find(
              (s) => s.id === subId
            );
            if (subcategoria) {
              handleDeleteClick('subcategoria', subId, subcategoria.nombre, categoriaId);
            }
          }}
        />
      </Box>

      {/* Categoria Form Dialog */}
      <CategoriaForm
        open={formState.mode === 'categoria-create' || formState.mode === 'categoria-edit'}
        categoria={formState.categoria || null}
        loading={loading}
        onSave={handleSaveCategoria}
        onClose={handleCloseForm}
      />

      {/* Subcategoria Form Dialog */}
      <SubcategoriaForm
        open={formState.mode === 'subcategoria-create' || formState.mode === 'subcategoria-edit'}
        subcategoria={formState.subcategoria || null}
        loading={loading}
        onSave={handleSaveSubcategoria}
        onClose={handleCloseForm}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que deseas eliminar "{deleteConfirm?.name}"?
          </Typography>
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
            Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
